/**
 * FilterSection component
 * Provides filtering UI for hotel results, including room, brand, benefit, and map view toggles.
 * Handles filter state, configuration, and rendering of filter controls.
 */

import React, { useMemo } from "react";
import { useLocale } from "next-intl";
import { SearchAvailableRoom } from "../../search-avaliable/SearchAvailableRoomMobile";
import { HotelBenefitListQuery, SetOfTag } from "@/types/generated";
import { FilterAvailableRoom } from "../../search-avaliable/FilterAvailableRoom";
import {
  handleBenefitListData,
  handleBrandListData,
  getSearchRoomData,
} from "./utils";
import { Button } from "@/components/atoms/button";
import useSearchFormStore from "@/store/searchFormStore/searchFormStore";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { HotelBrandItemNullable } from "@app/[locale]/brands/[brand_slug]/page";
import { SelectOption } from "../../search-avaliable/SearchAvailableRoom";
import { useParams } from "next/navigation";
import { useHasHydrated } from "@/hooks/useHasHydrated/useHasHydrated";
import { Skeleton } from "@/components/atoms/skelton";
import { IHotelListSliderProps } from "../../hotel-list-slider";
export interface FilterrSectionProps {
  benefitList?: HotelBenefitListQuery;
  brandList?: HotelBrandItemNullable[];
  onFilterChange: (f: SearchAvailableRoom | null) => void;
  filterState: SearchAvailableRoom | null;
  defaultValue?: SearchAvailableRoom | null;
  currentBrand?: HotelBrandItemNullable;
  isMapView?: boolean;
  setIsMapView?: React.Dispatch<React.SetStateAction<boolean>>;
  className?: string;
  isShowFilterBannerPage?: boolean | null;
  listOption: SelectOption[] | { label: string; options: SelectOption[] }[];
  isFirstCityChange?: React.MutableRefObject<boolean>;
  SetSubmitClicked: React.Dispatch<React.SetStateAction<boolean>>;
  isHotelListPage?: boolean;
  isHotelOnSenPage?: boolean;
  currentTag?: SetOfTag;
  hotelSliderSections?: IHotelListSliderProps[];
  submitted?: boolean;
}

const FilterSection: React.FC<FilterrSectionProps> = ({
  benefitList,
  brandList,
  onFilterChange,
  filterState,
  defaultValue,
  currentBrand,
  isMapView,
  setIsMapView,
  className,
  isShowFilterBannerPage,
  listOption,
  isFirstCityChange,
  SetSubmitClicked,
  isHotelListPage,
  isHotelOnSenPage,
  currentTag,
  hotelSliderSections,
  submitted,
}) => {
  const locale = useLocale();
  const params = useParams();
  const { configurations: searchAvailableRoom } = useSearchFormStore();
  const hasHydrated = useHasHydrated();

  const searchRoomData = getSearchRoomData(searchAvailableRoom, params);
  const {
    configurations,
    rooms_configurations,
    booking,
    ages_configurations,
    tripla_section,
  } = searchRoomData || {};
  const {
    checkin_label,
    checkout_label,
    promo_code_label,
    promo_code_placeholder,
  } = configurations ?? {};
  const { maximum_adults, maximum_child_age, maximum_children, maximum_rooms, default_adult_count } =
    rooms_configurations ?? {};
  const {
    tripla_add_room_label,
    tripla_bed_label,
    tripla_room_adults_label,
    tripla_room_children_label,
    tripla_room_name_label,
    tripla_with_bed_label,
    tripla_without_bed_label,
  } = tripla_section ?? {};
  const { booking_button_text } = booking ?? {};

  const { ages_label, age_placeholder, single_child_label } =
    ages_configurations ?? {};

  /**
   * Memoize benefit and brand options for filter controls
   */
  const benefitsOption = useMemo(() => {
    return handleBenefitListData(benefitList);
  }, [benefitList]);
  const brandsOption = useMemo(() => {
    return handleBrandListData(brandList);
  }, [brandList]);
  if (!hasHydrated)
    return (
      <div
        className={cn(
          "mt-10 flex !w-full justify-center space-x-4 md:w-auto md:justify-end md:space-x-2",
          className,
        )}
      >
        <Skeleton className="h-[44px] w-[150px]" />
        <Skeleton className="h-[44px] w-[150px]" />
      </div>
    );
  /**
   * Render filter controls and map view toggle
   */
  return (
    <div
      className={cn(
        "mt-5 flex !w-full justify-center space-x-2 md:mt-0 md:w-auto md:justify-end md:space-x-4",
        className,
      )}
    >
      {((isShowFilterBannerPage && currentBrand) || !currentBrand) && (
        <FilterAvailableRoom
          locale={locale}
          className={""}
          trigger={
            <Button
              hierarchy="tonal"
              size="lg"
              className={`flex min-h-9 items-center justify-center gap-1 bg-[var(--fill-base-gray)] md:min-h-11 md:gap-2 md:text-base ${isMapView ? "min-w-[120px] px-2 text-[12px] md:px-4" : "min-w-[150px] text-sm"}`}
            >
              <Image src="/filter.svg" alt={""} width={16} height={16} />
              <div className="text-[var(--text-gray-normal)]">絞り込み</div>
            </Button>
          }
          SetSubmitClicked={SetSubmitClicked}
          listOption={listOption}
          configurations={{
            checkinLabel: checkin_label ?? "",
            checkoutLabel: checkout_label ?? "",
            promoCodeLabel: promo_code_label ?? "",
            promoCodePlaceholder: promo_code_placeholder ?? "",
          }}
          roomsConfigurations={{
            adultLabel: tripla_room_adults_label ?? "",
            childrenLabel: tripla_room_children_label ?? "",
            addRoomButtonText: tripla_add_room_label ?? "",
            maximumAdults: maximum_adults ?? "",
            maximumChildAge: maximum_child_age ?? "",
            maximumChildren: maximum_children ?? "",
            maximumRooms: maximum_rooms ?? "",
            singleRoomLabel: tripla_room_name_label ?? "",
            bedLabel: tripla_bed_label ?? "",
            withBedLabel: tripla_with_bed_label ?? "",
            withoutBedLabel: tripla_without_bed_label ?? "",
            default_adult_count: default_adult_count ?? "",
          }}
          booking={{
            bookingButtonText: booking_button_text ?? "",
          }}
          agesConfigurations={{
            agesLabel: ages_label ?? "",
            agePlaceholder: age_placeholder ?? "",
            singleChildLabel: single_child_label ?? "",
          }}
          benefitOption={benefitsOption}
          brandOption={brandsOption}
          onFilterChange={onFilterChange}
          filterState={filterState}
          defaultValue={defaultValue}
          currentBrand={currentBrand}
          isFirstCityChange={isFirstCityChange}
          isHotelListPage={isHotelListPage}
          isHotelOnSenPage={isHotelOnSenPage}
          currentTag={currentTag}
          hotelSliderSections={hotelSliderSections}
        />
      )}
      <Button
        onClick={() => setIsMapView?.(!isMapView)}
        size={"lg"}
        className={`flex min-h-9 items-center justify-center gap-1 sm:min-w-40 md:gap-2 ${isMapView ? "min-w-[160px] px-2 text-[12px] md:px-4" : "min-w-[150px] text-sm"} md:min-h-11 md:text-base`}
      >
        <div className="relative h-4 w-4">
          <Image
            src="/locationPin.svg"
            alt={""}
            fill
            className="object-cover"
          />
        </div>
        {isMapView ? <>マップビュー - 選択済み</> : <>マップ表示</>}
      </Button>
    </div>
  );
};

export default FilterSection;
